import { useState } from 'react'

import { clearProjectNotBilled, markProjectNotBilled } from '@/lib/api/projects'

/**
 * SOLY-REGIE-002 — le geste « Retouche gratuite », UNE fois.
 *
 * Il est offert depuis deux surfaces : la fiche projet et la ligne de « À facturer ». Deux
 * implémentations, c'est deux endroits où oublier de rafraîchir, deux façons de traiter un 403, et
 * à terme deux comportements pour le même bouton. Le hook porte l'appel et l'état ; l'appelant
 * fournit ce qu'il faut faire APRÈS (`onSettled`), parce que c'est la seule chose qui diffère
 * vraiment entre les deux écrans — l'un recharge sa fiche, l'autre sa liste.
 *
 * Réversible des deux côtés : c'est le patron Mark/Clear du backend, et c'est ce qui permet à la
 * modale de ne pas dramatiser (« Tu pourras revenir sur cette décision. »).
 */
export function useNotBilledDecision(onSettled: () => void | Promise<void>) {
  const [isPending, setIsPending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const run = async (action: () => Promise<void>) => {
    setIsPending(true)
    setError(null)
    try {
      await action()
      await onSettled()
    } catch (e) {
      // Le message du serveur est plus utile que le nôtre : sans `invoice:write` il dit
      // pourquoi, et c'est exactement ce que la personne a besoin de lire.
      setError(e instanceof Error ? e.message : 'La décision n’a pas pu être enregistrée.')
    } finally {
      setIsPending(false)
    }
  }

  return {
    isPending,
    error,
    markNotBilled: (projectId: string) => run(() => markProjectNotBilled(projectId)),
    clearNotBilled: (projectId: string) => run(() => clearProjectNotBilled(projectId)),
  }
}
